import type { Router, RouteRecordRaw } from 'vue-router';
import type { ResolvedMonospaceSpace } from '#layers/data-model/public';
import type { SpaceRouteUpdates, UpdateSpaceRoutesOptions } from '#layers/spaces/public';
import { cleanDoubleSlashes } from 'ufo';
import { getSpaceFirstPage, getSpacePageRouteParams, SpacePageRouteMarker, SpaceRouteMarker } from '#layers/spaces/public';

interface CreateSpaceRoutesOptions {
	spaces: ResolvedMonospaceSpace[];
}

export function createSpaceRoutes({
	spaces,
}: CreateSpaceRoutesOptions): RouteRecordRaw[] {
	const routes: RouteRecordRaw[] = [];

	// Build a map of routes
	for (const space of spaces) {
		const firstPage = getSpaceFirstPage(space);
		const spaceRoute: RouteRecordRaw = {
			name: space.id,
			path: cleanDoubleSlashes([`:spaceSlug(.*)?-${space.id}`, space.route].map((p) => p.trim()).filter(Boolean).join('/')),
			component: () => import('#layers/ui-workspace/components/Space/SpaceRender.vue'),
			props: {
				id: space.id,
			},
			redirect: firstPage
				? () => ({
						name: firstPage.id,
						params: getSpacePageRouteParams(firstPage),
					})
				: undefined,
			meta: {
				space: {
					title: space.title,
					icon: space.icon || 'lucide:file',
					sort: space.sort,
				},
				layout: 'workspace',
				[SpaceRouteMarker]: true,
			},
			children: [],
		};
		routes.push(spaceRoute);

		// TODO page folders

		const pages = space.pages;

		// Construct page tree

		/**
		 * Route record name -> Children route records
		 */
		const pageTree = new Map<string, RouteRecordRaw[]>();

		for (const page of pages) {
			let childrenRoutes = pageTree.get(page.id);

			if (!childrenRoutes) {
				childrenRoutes = [];
				pageTree.set(page.id, childrenRoutes);
			}

			const pageRoute: RouteRecordRaw = {
				name: page.id,
				path: cleanDoubleSlashes([`:pageSlug(.*)?-${page.id}`, page.route].map((p) => p.trim()).filter(Boolean).join('/')),
				component: () => import('#layers/ui-workspace/components/Space/PageRender.vue'),
				props: {
					id: page.id,
					spaceId: space.id,
				},
				meta: {
					page: {
						id: page.id,
						title: page.title,
						icon: page.icon || 'lucide:file',
						sort: page.sort,
					},
					[SpacePageRouteMarker]: true,
				},
				children: childrenRoutes,
			};

			// Insert into tree
			if (page.parentPageId) {
				let parentChildren = pageTree.get(page.parentPageId);

				// In case the parent page route record has not been created yet
				if (!parentChildren) {
					parentChildren = [];
					pageTree.set(page.parentPageId, parentChildren);
				}

				parentChildren.push(pageRoute);
			}
			else {
				// 'Root' page of the space
				spaceRoute.children.push(pageRoute);
			}
		}

		spaceRoute.children.push({
			name: `${space.id}--catch-all`,
			path: ':path(.*)*',
			component: () => import('#layers/ui-workspace/components/Space/RouteNotFound.vue'),
		});
	}

	return routes;
}

export function createSpaceRouteUpdater(router: Router): SpaceRouteUpdates {
	function updateRoutes(options: UpdateSpaceRoutesOptions) {
		const routes = createSpaceRoutes(options);

		for (const route of routes) {
			router.addRoute('workspace-base', route);
		}

		if (!options.skipCurrentRouteUpdate) {
			// Force reloading the current route to apply the new routes records
			// to avoid stale record objects in the `currentRoute`
			router.replace(router.currentRoute.value.fullPath);
		}

		return {
			revert: () => {
				for (const route of routes) {
					router.removeRoute(route.name!);
				}
			},
		};
	}

	return {
		updateRoutes,
	};
}
